import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import ProtectedStaffRoute from '@/src/components/portal/ProtectedStaffRoute'
import { Helmet } from 'react-helmet-async'
import { lazy, Suspense, useEffect } from 'react'
import { ConsentProvider } from '@/src/context/ConsentContext'
import { destinoPorDominio } from '@/src/lib/hosts'
// Lazy load pages
const HomePage = lazy(() => import('@/src/components/pages/HomePage'))
const OwtliPage = lazy(() => import('@/src/pages/owtli/OwtliPage'))
const BlogPage = lazy(() => import('@/src/pages/blog/BlogPage'))
const BlogDetailPage = lazy(() => import('@/src/pages/blog/BlogDetailPage'))
const ServiciosPage = lazy(() => import('@/src/pages/servicios/ServiciosPage'))
const AvisoLegalPage = lazy(() => import('@/src/pages/legal/AvisoLegalPage'))
const PrivacidadPage = lazy(() => import('@/src/pages/legal/PrivacidadPage'))
const CookiesPage = lazy(() => import('@/src/pages/legal/CookiesPage'))
const HubOverviewPage = lazy(() => import('@/src/admin/hub/HubOverviewPage'))
const HubRevenuePage = lazy(() => import('@/src/admin/hub/HubRevenuePage'))
const HubInfraPage = lazy(() => import('@/src/admin/hub/HubInfraPage'))
const HubProductsPage = lazy(() => import('@/src/admin/hub/HubProductsPage'))
const HubSocialPage = lazy(() => import('@/src/admin/hub/HubSocialPage'))
const HubProspectsPage = lazy(() => import('@/src/admin/hub/HubProspectsPage'))
const HubReposPage = lazy(() => import('@/src/admin/hub/HubReposPage'))
const HubAuditPage = lazy(() => import('@/src/admin/hub/HubAuditPage'))
const OficinaPage = lazy(() => import('@/src/admin/oficina/OficinaPage'))
const NotFoundPage = lazy(() => import('@/src/pages/NotFoundPage'))

import { LOGIN_PATH } from '@/src/lib/userAccess'
const LoginPage = lazy(() => import('@/src/pages/admin/LoginPage'))

// Admin pages
const AdminLayout = lazy(() => import('@/src/pages/admin/AdminLayout'))
const AdminPostsPage = lazy(() => import('@/src/pages/admin/AdminPostsPage'))
const AdminSettingsPage = lazy(() => import('@/src/pages/admin/AdminSettingsPage'))

const PageLoader = () => <div className="w-cargando" aria-busy="true" />

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

function App() {
  const location = useLocation()
  // Una navegación dentro de la SPA no pasa por el edge: si la ruta es del otro dominio (la web desde el panel o el
  // panel desde la web), se va allí con una carga completa.
  const destino = destinoPorDominio(window.location.hostname, location.pathname, location.search + location.hash)
  useEffect(() => {
    if (destino) window.location.replace(destino)
  }, [destino])

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }

    if (window.location.hash) {
      window.scrollTo(0, 0)
      const hash = window.location.hash.replace('#', '')
      if (hash && !window.location.search.includes('section=')) {
        const newUrl = window.location.pathname + '?section=' + hash
        window.history.replaceState(null, '', newUrl)
      }
    }
  }, [])

  if (destino) return <PageLoader />

  return (
    <ConsentProvider>
      {/* Sin title, description ni canonical por defecto: el edge los sirve bien para cada URL y cada página pone los
          suyos. Un valor por defecto aquí declaraba la home como canonical mientras cargaba un artículo. */}
      <Helmet>
        <html lang="es" />
      </Helmet>

      <ScrollToTop />
      <div className="antialiased">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/nosotros" element={<OwtliPage />} />
            <Route path="/owtli" element={<Navigate to="/nosotros" replace />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/agendar" element={<Navigate to="/#cta" replace />} />
            <Route path="/axis" element={<Navigate to="/servicios" replace />} />
            <Route path="/owtlicore" element={<Navigate to="/servicios" replace />} />
            <Route path="/modulos/*" element={<Navigate to="/servicios" replace />} />
            <Route path="/servicios" element={<ServiciosPage />} />
            <Route path="/aviso-legal" element={<AvisoLegalPage />} />
            <Route path="/privacidad" element={<PrivacidadPage />} />
            <Route path="/cookies" element={<CookiesPage />} />

            {/* Acceso del equipo. El portal de clientes se retiró el 24/10/2026: sus rutas vuelven a la web. */}
            <Route path={LOGIN_PATH} element={<LoginPage />} />
            <Route path="/admin/login" element={<Navigate to={LOGIN_PATH} replace />} />
            <Route path="/cliente/*" element={<Navigate to="/" replace />} />
            <Route path="/dashboard/*" element={<Navigate to="/" replace />} />

            {/* Admin */}
            <Route
              path="/admin"
              element={
                <ProtectedStaffRoute>
                  <AdminLayout />
                </ProtectedStaffRoute>
              }
            >
              <Route index element={<HubOverviewPage />} />
              <Route path="oficina" element={<OficinaPage />} />
              <Route path="ingresos" element={<HubRevenuePage />} />
              <Route path="infraestructura" element={<HubInfraPage />} />
              <Route path="productos" element={<HubProductsPage />} />
              <Route path="redes" element={<HubSocialPage />} />
              <Route path="prospeccion" element={<HubProspectsPage />} />
              <Route path="repos" element={<HubReposPage />} />
              <Route path="accesos" element={<HubAuditPage />} />
              <Route path="posts" element={<AdminPostsPage />} />
              <Route path="clientes/*" element={<Navigate to="/admin" replace />} />
              <Route path="licencias" element={<Navigate to="/admin" replace />} />
              <Route path="settings" element={<AdminSettingsPage />} />
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
    </ConsentProvider>
  )
}

export default App
